import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { SelectedScreenType } from "@/Views/Virtual/SelectedScreensPopup/SelectedScreensPopup";
import { useSelectedScreensStates } from "./useSelectedScreensStates";
import { useDownloadImagesMutation } from "@/features/auth/api/authAPI";
import { useCurrentOrganization } from "@/Hooks";

interface UseSelectedScreensDownloadProps {
  type: SelectedScreenType;
}

export const useSelectedScreensDownload = (
  props: UseSelectedScreensDownloadProps,
) => {
  const { type } = props;
  const organization = useCurrentOrganization();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadImages] = useDownloadImagesMutation();
  const { screens } = useSelectedScreensStates({ type });

  const handleDownload = useCallback(async () => {
    setIsDownloading(true);

    try {
      const appVideoAnimations: number[] = [];
      const replayScreens: number[] = [];
      const storeScreens: number[] = [];
      const obScreens: number[] = [];

      // Add screen IDs to the appropriate array based on type
      if (type === "appReplays" || type === "searchScreens") {
        replayScreens.push(...screens.map((screen) => screen.id));
      } else if (type === "appStoreScreens") {
        storeScreens.push(...screens.map((screen) => screen.id));
      } else if (type === "onboardingScreens") {
        obScreens.push(...screens.map((screen) => screen.id));
      } else if (type === "animationScreens") {
        appVideoAnimations.push(...screens.map((screen) => screen.id));
      }

      if (!organization?.uuid) {
        toast.error("Organization ID not found");
        return;
      }

      // Prepare the payload with only non-empty arrays
      const payload: any = {
        organizationId: organization.uuid,
      };

      // Only include non-empty arrays in the payload
      if (appVideoAnimations.length > 0) {
        payload.app_video_animations = appVideoAnimations;
      }
      if (replayScreens.length > 0) {
        payload.replay_screens = replayScreens;
      }
      if (storeScreens.length > 0) {
        payload.store_screens = storeScreens;
      }
      if (obScreens.length > 0) {
        payload.ob_screens = obScreens;
      }

   
      const screenCount = screens.length;
      const typeMap = {
        appReplays: "App_Replays",
        appStoreScreens: "App_Store",
        onboardingScreens: "Onboarding",
        animationScreens: "Animation",
      };
      const typeLabel = typeMap[type] || type;

      const filename = `${typeLabel}_${screenCount}_screens.zip`;

      await toast
        .promise(downloadImages(payload).unwrap(), {
          pending: "Preparing your download...",
          success: "Screens downloaded successfully",
          error: "Failed to download screens",
        })
        .then((blob) => {
          // Create a URL for the blob
          const url = window.URL.createObjectURL(blob);

          // Create a temporary link element
          const link = document.createElement("a");
          link.href = url;
          link.download = filename;

          // Trigger the download
          document.body.appendChild(link);
          link.click();

          // Clean up
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        });
    } catch (error) {
      console.error("Failed to download screens:", error);
    } finally {
      setIsDownloading(false);
    }
  }, [props.type, screens, downloadImages, organization]);

  return {
    handleDownload,
    isDownloading,
  };
};
